import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ApiError } from '../../lib/api';
import { usePublicProp, propPublicThumbUrl, propPublicFileUrl } from './api';

// Token z URL /vyloha/<token> (běží mimo router i AuthGate).
function tokenFromPath(): string {
  const m = window.location.pathname.match(/\/vyloha\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : '';
}

export default function ShowcasePage() {
  const token = tokenFromPath();
  const { data, isLoading, error } = usePublicProp(token);
  const [lightbox, setLightbox] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100">
      <div className="mx-auto max-w-3xl px-4 py-10">
        {isLoading && <p className="text-sm text-zinc-500">Načítám…</p>}

        {error && (
          <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-10 text-center">
            <h1 className="text-lg font-semibold mb-1">Stránka není dostupná</h1>
            <p className="text-sm text-zinc-500">
              {(error as ApiError).status === 404
                ? 'Tento veřejný odkaz je neplatný nebo byl vypnut.'
                : (error as ApiError).message}
            </p>
          </div>
        )}

        {data && (
          <>
            <header className="mb-8">
              <p className="text-xs uppercase tracking-wide text-zinc-400">{data.studioName}</p>
              <h1 className="mt-1 text-3xl font-bold tracking-tight">{data.prop.name}</h1>
              {(data.projectName || data.production) && (
                <p className="mt-1 text-sm text-zinc-500">
                  {data.projectName}
                  {data.projectName && data.production ? ' · ' : ''}
                  {data.production}
                </p>
              )}
              {data.prop.description && <p className="mt-3 text-zinc-600 dark:text-zinc-300">{data.prop.description}</p>}
            </header>

            {data.photos.length > 0 && (
              <section className="mb-8">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {data.photos.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setLightbox(propPublicFileUrl(token, p.id))}
                      className="aspect-square overflow-hidden rounded-md border border-zinc-200 dark:border-zinc-800"
                    >
                      <img src={propPublicThumbUrl(token, p.id)} alt={p.caption ?? ''} loading="lazy" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              </section>
            )}

            {data.prop.story && (
              <section
                className="mb-8 space-y-3 text-zinc-700 dark:text-zinc-300
                  [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mt-6
                  [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-6
                  [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-4
                  [&_p]:leading-relaxed
                  [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1
                  [&_a]:text-blue-600 [&_a]:dark:text-blue-400 [&_a]:underline
                  [&_strong]:font-semibold [&_img]:rounded-lg
                  [&_blockquote]:border-l-4 [&_blockquote]:border-zinc-300 [&_blockquote]:dark:border-zinc-700 [&_blockquote]:pl-4 [&_blockquote]:italic"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.prop.story}</ReactMarkdown>
              </section>
            )}

            <footer className="mt-10 border-t border-zinc-200 dark:border-zinc-800 pt-4 text-xs text-zinc-400">
              Vyrobilo {data.studioName}
              {data.studioWeb && (
                <>
                  {' · '}
                  <a href={data.studioWeb.startsWith('http') ? data.studioWeb : `https://${data.studioWeb}`} target="_blank" rel="noreferrer" className="hover:underline">
                    {data.studioWeb}
                  </a>
                </>
              )}
            </footer>
          </>
        )}
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" className="max-h-full max-w-full rounded shadow-xl" />
        </div>
      )}
    </div>
  );
}
